import React, { useContext, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { PaymentTypeEnum, useInitiateCheckoutMutation, usePaymentMethodQuery } from 'graphQL/dinein/generate/operations'
import { WatchQueryFetchPolicy } from '@apollo/client'
import { SubPageName } from 'enums/dineIn'
import PaymentMethodUI from './PaymentMethodUI'
import { dineInClient } from 'variables/services'
import { AppContext } from 'contexts/AppProvider'
import { CurrentOrder } from 'hooks/buffet/usePaymentMethod'
import { useSelector } from 'react-redux'
import { buffetSelector, resetBuffetModal } from 'store/slices/buffetSlice'
import useValidateCurrentOrder, { OrderInvalidEnum } from 'hooks/buffet/useValidateCurrentOrder'
import useHandleBuffetOrder from 'hooks/buffet/useHandleBuffetOrder'
import { ModalEnum } from 'components/buffet/BuffetModals'
import { ScreenEnum } from 'components/buffet/BuffetOverlayPage'
import { useAppDispatch } from 'store'

const PaymentMethodController = () => {
  const { brand } = useContext(AppContext)
  const [currentOrder, setCurrentOrder] = useState<CurrentOrder>()
  const buffet = useSelector(buffetSelector)
  const dispatch = useAppDispatch()
  const [paymentMethods, setPaymentMethods] = useState<PaymentTypeEnum[]>([])
  const [vendorID, setVendorID] = useState<number>()
  const history = useHistory()
  const requestOption = {
    client: dineInClient,
    fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
  }
  const { validateCurrentOrder } = useValidateCurrentOrder()
  const { handleValidateResult } = useHandleBuffetOrder({
    skipModal: [ModalEnum.TimeUp, ModalEnum.ReadyToCheckout, ModalEnum.ContinueCheckout],
    skipScreen: [ScreenEnum.CreditCardFailed, ScreenEnum.ResubmitPayment],
  })
  const [initiateCheckout] = useInitiateCheckoutMutation({
    client: dineInClient,
  })

  useEffect(() => {
    return () => {
      dispatch(resetBuffetModal())
    }
  }, [])
  const { refetch } = usePaymentMethodQuery({
    ...requestOption,
    variables: {
      vendorID: Number(brand?.id),
      orderID: buffet.table.orderID,
      tableID: buffet.table.tableID,
    },
    onCompleted: async (result) => {
      const { currentOrder, vendorSetting, vendor } = result
      setCurrentOrder(currentOrder)
      setPaymentMethods(vendorSetting.paymentMethods)
      setVendorID(vendor.id)
      const validateResult = customValidateOrder()
      if (validateResult.invalid === OrderInvalidEnum.ReadyToCheckout && result.currentOrder.userID === 0) {
        await initiateCheckout({
          variables: {
            orderID: buffet.table.orderID,
            override: true,
          },
        })
        await refetch()
      }
    },
  })

  const refetchPayment = async () => {
    const result = await refetch()
    return result.data
  }

  const customValidateOrder = () => {
    const validateResult = validateCurrentOrder(buffet.currentOrder)
    if (
      validateResult.isOK ||
      validateResult.invalid === OrderInvalidEnum.OwnerNeedToCheckout ||
      validateResult.invalid === OrderInvalidEnum.CreditCardFailed ||
      validateResult.invalid === OrderInvalidEnum.PaymentIsNotCompleted
    ) {
      return { ...validateResult, isOK: true }
    } else {
      handleValidateResult(validateResult, buffet.currentOrder)
      return validateResult
    }
  }

  return vendorID && currentOrder ? (
    <PaymentMethodUI
      handleBack={() => history.push(SubPageName.Checkout)}
      vendorID={vendorID}
      currentOrder={currentOrder}
      paymentMethods={paymentMethods}
      refetchPayment={refetchPayment}
    />
  ) : null
}

export default PaymentMethodController
